import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import React, { Fragment, FunctionComponent, useContext } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import ROUTES from '../../../routes';
import colors from '../../../themes/main-colors';
import {
  toDateString,
  toCurrencyString,
  toCurrencyDisplayFormat,
  toSentenceCase,
} from '../../../util/format';
import {
  BillInfo,
  AutoPayInfo,
  ViewBillDetails,
  AccountType,
} from '../../../__generated__/pge-types';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import { LanguageContext } from '../../../providers/LanguageProvider';
import DownloadBillButton from '../download-bill-button';

export interface CurrentBillPaymentSummaryProps {
  totalPendingCharges?: number | null;
  amountCreditDisplay: 'CR' | 'Zero';
  autoPay: AutoPayInfo | null;
  billInfo: BillInfo | null | undefined;
  isMobile: boolean;
  viewBillDetails: ViewBillDetails | null | undefined;
  isSummaryBillAccount: boolean | undefined;
  isPendingDisconnect: boolean | undefined;
  isNewAccount: boolean | null | undefined;
}

function buildPaymentMessage(billInfo: BillInfo | null | undefined) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const {
    oneTimeFuturePaymentScheduled,
    multipleFuturePaymentsScheduled,
    lastPaymentDate,
    lastPaymentAmount,
    futurePaymentDate,
  } = billInfo || {};

  if (oneTimeFuturePaymentScheduled && Boolean(futurePaymentDate)) {
    const futureMessage = t('LAST_PAYMENT_ONE_FUTURE', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(lastPaymentDate || ''),
      FUTURE_AMOUNT: toCurrencyString(lastPaymentAmount || 0, false),
      FUTURE_DATE: toDateString(futurePaymentDate || ''),
    });

    if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
      return `${t('LAST_PAYMENT_OF', {
        AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
        DATE_OF_PAYMENT: toDateString(
          lastPaymentDate || '',
          'MMM. dd, yyyy',
          language,
        ),
      })} ${futureMessage}`;
    } else {
      return futureMessage;
    }
  } else if (multipleFuturePaymentsScheduled) {
    return t('LAST_PAYMENT_MULTIPLE_FUTURE');
  } else if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
    return t('LAST_PAYMENT_OF', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(
        lastPaymentDate || '',
        'MMM. dd, yyyy',
        language,
      ),
    });
  }

  return t('LAST_PAYMENT_YET_TO_BE_BILLED');
}

function buildPaymentDate(
  billInfo: BillInfo | null | undefined,
  autoPay: AutoPayInfo | null,
) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const isEnrolledInAutopay = Boolean(autoPay?.isEnrolled);
  if (isEnrolledInAutopay && autoPay?.draftDate) {
    return {
      title: t('WILL_BE_DEDUCTED'),
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : '---',
    };
  } else {
    return {
      title: t('DUE_DATE') + ': ',
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : t('NOT_YET_AVAILABLE'),
    };
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: (props: { hideBorder: boolean }) => ({
      backgroundColor: colors.white,
      paddingBottom: '16px',
      borderRadius: '3px 3px 0px 0px',
      marginTop: '2px',
      borderBottom: `1px solid ${
        props.hideBorder ? 'transparent' : colors.lightGray1
      }`,
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    }),
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    datesStyles: {
      color: colors.noirBlur,
      '& p': {
        marginTop: '0px !important',
      },
    },
    colorTextPastdue: {
      color: colors.roseRed,
      fontWeight: 'bold',
    },
    columnview: {
      padding: '16px 16px 0 16px',
    },
    mobileColumnview: {
      padding: '16px 0 0 0',
    },
    boxContainer: {
      backgroundColor: colors.white,
    },
    autoPayLinkStyle: {
      marginTop: '1rem',
      textAlign: 'center',
    },
    tempBillingMessage: {
      fontSize: '1rem',
      padding: '1rem 1rem 0 1rem',
    },
    tempBillingMessageMob: {
      fontSize: '1rem',
      paddingTop: '1rem',
    },
    autoPayBtn: {
      padding: '8px 10px',
      width: '100%',
      borderColor: colors.sparkBlue,
      color: colors.sparkBlue,
      '&:hover': {
        backgroundColor: 'transparent',
        borderColor: colors.noirBlur,
        color: colors.noirBlur,
      },
    },
  }),
);

const CurrentBillPaymentSummary: FunctionComponent<CurrentBillPaymentSummaryProps> = ({
  totalPendingCharges,
  billInfo,
  autoPay,
  amountCreditDisplay,
  viewBillDetails,
  isSummaryBillAccount,
  isPendingDisconnect,
  isMobile,
  isNewAccount,
}) => {
  function buildPayButton(
    billInfoDetails: BillInfo | null | undefined,
    autoPayDetails: AutoPayInfo | null,
  ) {
    const isEnrolledInAutopay = Boolean(autoPayDetails?.isEnrolled);

    if (isEnrolledInAutopay) {
      return {
        text: t('CHANGE_AUTO_PAY_SETTINGS'),
        route: ROUTES.AUTO_PAY,
        enabled: true,
        colorStyle: colors.white,
        textStyle: colors.sparkBlue,
        testid: 'auto-pay-button',
      };
    } else {
      return {
        text: t('PAY_BILL'),
        route: ROUTES.PAYMENT,
        enabled: Number(billInfoDetails?.amountDue) > 0,
        colorStyle: colors.orange,
        textStyle: colors.white,
        testid: 'pay-bill-button',
      };
    }
  }

  const convertedStartDate = toDateString(
    viewBillDetails?.billingPeriodStartDate,
    'MM/dd/yyyy',
  );
  const convertedEndDate = toDateString(
    viewBillDetails?.billingPeriodEndDate,
    'MM/dd/yyyy',
  );
  const { t, richT, inlineRichT } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const classes = useStyles({
    hideBorder:
      currentPath === ROUTES.VIEW_BILL ||
      currentPath === ROUTES.QUICK_VIEWBILL ||
      !!isNewAccount,
  });
  const message = buildPaymentMessage(billInfo);
  const dueDateInfo = buildPaymentDate(billInfo, autoPay);
  const payButton = buildPayButton(billInfo, autoPay);

  const contents = (
    <Fragment>
      <Grid container direction="row" className={classes.container}>
        <Grid
          item
          xs={isMobile ? 12 : 8}
          className={!isMobile ? classes.columnview : classes.mobileColumnview}
        >
          <Grid container className={classes.boxContainer}>
            <Grid item xs={12}>
              {isPendingDisconnect ? (
                <Typography
                  className={classes.colorTextPastdue}
                  data-testid="past-due-label"
                >
                  {t('PAST_DUE')}
                </Typography>
              ) : Boolean(autoPay?.isEnrolled) ? (
                <Typography
                  className={classes.colorTextPrimary}
                  data-testid="auto-pay-label"
                >
                  {t('AUTO_PAY_AMOUNT')}
                </Typography>
              ) : (
                <Typography
                  className={classes.colorTextPrimary}
                  data-testid="amount-due-label"
                >
                  {t('AMOUNT_DUE')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                className={
                  isPendingDisconnect
                    ? classes.colorTextPastdue
                    : classes.colorTextPrimary
                }
                variant="h1"
                style={{ fontWeight: 'bold' }}
              >
                {`${
                  billInfo?.amountDue !== null &&
                  billInfo?.amountDue !== undefined
                    ? '$' +
                      toCurrencyDisplayFormat(
                        billInfo?.amountDue,
                        true,
                        amountCreditDisplay,
                      )
                    : '--'
                }`}
              </Typography>
            </Grid>
            <Grid item container direction="row" spacing={1}>
              <Grid item>
                <Typography
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {toSentenceCase(dueDateInfo.title) + ' '}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  data-testid="current-bill-pay-summary-due-date"
                  style={{ fontWeight: 'bold' }}
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {dueDateInfo.value}
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              {viewBillDetails && viewBillDetails.billingPeriodStartDate ? (
                <Typography
                  className={classes.datesStyles}
                  variant="h6"
                  data-testid="service-startdate-enddate-message"
                >
                  {richT('SERVICE_FROM_START_END_DATE', {
                    START_DATE: convertedStartDate,
                    END_DATE: convertedEndDate,
                  })}
                </Typography>
              ) : (
                <Typography
                  className={classes.colorTextPrimary}
                  variant="h6"
                  data-testid="no-service-message"
                >
                  {richT('SERVICE_NOT_AVAILABLE')}
                </Typography>
              )}
            </Grid>
            {isPendingDisconnect && (
              <Grid direction="row">
                <Typography
                  className={classes.colorTextPrimary}
                  variant="body2"
                  data-testid="past-due-message"
                >
                  {t('PAST_DUE_MESSAGE', {
                    CURRENT_CHARGES: String(
                      viewBillDetails?.totalCurrentCharges,
                    ),
                    PAST_DUE: String(viewBillDetails?.totalBalanceAfterBill),
                  })}
                </Typography>
              </Grid>
            )}

            {totalPendingCharges ? (
              totalPendingCharges < 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    {richT('CURRENT_BILL_PAY_BILL_TEXT_WITH_PENDING_CREDIT', {
                      AMOUNT: toCurrencyDisplayFormat(
                        totalPendingCharges,
                        true,
                        'CR',
                      ),
                      PAYMENT_HISTORY_URL: ROUTES.PAYMENT_HISTORY,
                    })}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    {richT('CURRENT_BILL_PAY_BILL_TEXT_WITH_PENDING_CHARGE', {
                      AMOUNT: toCurrencyDisplayFormat(
                        totalPendingCharges,
                        true,
                        'CR',
                      ),
                      PAYMENT_HISTORY_URL: ROUTES.PAYMENT_HISTORY,
                    })}
                  </Typography>
                </Grid>
              )
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2">
                  {t('CURRENT_BILL_PAY_BILL_TEXT')}
                </Typography>
              </Grid>
            )}
            {Boolean(viewBillDetails) && (
              <Grid item xs={12} data-testid="payment-message">
                <Typography variant="body2">{message}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={isMobile ? 12 : 4}
          className={!isMobile ? classes.columnview : classes.mobileColumnview}
        >
          {billInfo && billInfo.billDetails && (
            <Grid item style={{ marginTop: '1rem' }}>
              <Button
                fullWidth
                data-testid={payButton.testid}
                onClick={() => navigate(payButton.route)}
                disabled={!payButton.enabled}
                size={'large'}
                variant="outlined"
                style={{
                  background: payButton.enabled
                    ? payButton.colorStyle
                    : colors.lightGray1,
                  color: payButton.textStyle,
                  borderRadius: isMobile ? '50px' : '4px',
                }}
              >
                {payButton.text}
              </Button>
            </Grid>
          )}
          {currentPath !== ROUTES.VIEW_BILL &&
            currentPath !== ROUTES.QUICK_VIEWBILL && (
              <Grid item style={{ marginTop: '1rem' }}>
                <Button
                  fullWidth
                  data-testid={'view_bill_button'}
                  onClick={() => navigate(ROUTES.VIEW_BILL)}
                  size={'large'}
                  variant="outlined"
                  style={{
                    whiteSpace: 'nowrap',
                    background: colors.sparkBlue,
                    color: colors.white,
                    borderRadius: isMobile ? '50px' : '4px',
                  }}
                >
                  {t('CURRENT_USE_VIEW_BILL_DETAILS')}
                </Button>
              </Grid>
            )}
          {(currentPath === ROUTES.VIEW_BILL ||
            currentPath === ROUTES.QUICK_VIEWBILL) &&
            !Boolean(autoPay?.isEnrolled) && (
              <Grid item style={{ marginTop: '1rem' }}>
                <Button
                  fullWidth
                  data-testid={'setup_auto_pay'}
                  onClick={() => navigate(ROUTES.AUTO_PAY)}
                  size={'large'}
                  variant="outlined"
                  className={classes.autoPayBtn}
                >
                  {t('SETUP_AUTOPAY')}
                </Button>
              </Grid>
            )}
          {viewBillDetails && !isNewAccount && (
            <Grid item style={{ marginTop: '1rem' }}>
              <DownloadBillButton
                isSummaryBillAccount={isSummaryBillAccount}
                billDetails={viewBillDetails}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Fragment>
  );

  return contents;
};
export default CurrentBillPaymentSummary;
